/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\TelephonyStatsWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useContext } from 'react';
import { Phone, PhoneCall, Users, Radio } from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';

interface TelephonyData {
    eslConnected: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    activeCalls: any[];
    totalExtensions?: number;
    extensionStatuses: Record<string, { status: string }>;
    gatewayCallCounts: Record<string, number>;
}

interface TelephonyStatsWidgetProps {
    config?: Record<string, any>;
    onConfigChange?: (config: Record<string, any>) => void;
    isEditMode?: boolean;
}

// Default widget preferences for card styling
const DEFAULT_CARD_COLOR = '#1e293b';
const DEFAULT_BORDER_COLOR = '#334155';

// Card wrapper for each stat (matches GaugeCard in SystemStatsWidget)
const StatCard = ({ children }: { children: React.ReactNode }) => {
    const widgetPrefs = useContext(WidgetContext);
    const cardColor = widgetPrefs?.cardColor || DEFAULT_CARD_COLOR;
    const borderColor = widgetPrefs?.borderColor || DEFAULT_BORDER_COLOR;

    return (
        <div
            className="flex items-center justify-center p-1 rounded-lg w-full h-full overflow-hidden"
            style={{
                backgroundColor: cardColor,
                border: `1px solid ${borderColor}`
            }}
        >
            {children}
        </div>
    );
};

export function TelephonyStatsWidget({ config, isEditMode }: TelephonyStatsWidgetProps) {
    const [data, setData] = useState<TelephonyData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const protocol = window.location.protocol;
                const host = window.location.host;
                const response = await fetch(`${protocol}//${host}/monitoring-api/api/dashboard`);

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data?.telephony) {
                        setData(result.data.telephony);
                        setError(null);
                    } else {
                        setError('No telephony data');
                    }
                } else {
                    setError('API error');
                }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                setError('Failed to load stats');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();

        // Refresh every 5 seconds
        const interval = setInterval(fetchStats, 5000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="h-full flex items-center justify-center text-muted-foreground">
                <span className="text-sm">{error || 'No data available'}</span>
            </div>
        );
    }

    // Calculate stats from data
    const activeCalls = data.activeCalls?.length || 0;

    // Count registered extensions
    const extensionEntries = Object.entries(data.extensionStatuses || {});
    const registeredExtensions = extensionEntries.filter(
        ([_, ext]) => ext.status === 'registered' || ext.status === 'busy' || ext.status === 'ringing'
    ).length;
    const totalExtensions = data.totalExtensions ?? extensionEntries.length;

    // Count total gateway calls
    const channelCount = Object.values(data.gatewayCallCounts || {}).reduce((sum, count) => sum + count, 0);

    const items = [
        {
            icon: PhoneCall,
            label: 'Active Calls',
            value: activeCalls,
            color: activeCalls > 0 ? 'text-green-500' : 'text-muted-foreground'
        },
        {
            icon: Users,
            label: 'Extensions',
            value: `${registeredExtensions}/${totalExtensions}`,
            color: 'text-blue-500'
        },
        {
            icon: Radio,
            label: 'Gateway Calls',
            value: channelCount,
            color: channelCount > 0 ? 'text-purple-500' : 'text-muted-foreground'
        },
    ];

    return (
        <div className="h-full flex flex-col">
            <div className="flex-1 flex items-stretch justify-center gap-2 px-2 overflow-hidden mb-2">
                {items.map(({ icon: Icon, label, value, color }) => (
                    <div key={label} className="flex-1 min-w-0">
                        <StatCard>
                            <div className="flex flex-col items-center justify-center w-full h-full min-h-0">
                                <div className="relative h-full aspect-square max-h-[120px] p-2 flex flex-col items-center justify-center">
                                    <Icon className={`w-1/3 h-1/3 mb-1 ${color}`} />
                                    <span className={`text-xl font-bold ${color.replace('text-', 'text-') === 'text-muted-foreground' ? 'text-foreground' : color}`}>
                                        {value}
                                    </span>
                                </div>
                                <span className="text-xs text-muted-foreground font-medium truncate max-w-full">{label}</span>
                            </div>
                        </StatCard>
                    </div>
                ))}
            </div>

            {/* ESL Connection Status */}
            <div className="flex items-center justify-center gap-3 py-1 px-3 border-t border-border/50">
                <div className={`w-2 h-2 rounded-full ${data.eslConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-xs text-muted-foreground">
                    ESL: {data.eslConnected ? 'Connected' : 'Disconnected'}
                </span>
            </div>
        </div>
    );
}
